import { ICourseDelivery, ICoursePickup, Iproduct } from "domains/Course/data/types";
import { generateReference } from "domains/Course/data/utils";
import { isDateValid, validateDeliveryDate } from "domains/Course/utils";
import { ClientRefsAddress } from "generated/graphql";
import React, { useEffect, useMemo } from "react";
import { formatDate } from "utils";

const initPickups: ICoursePickup[] = [
  {
    id: null,
    reference: generateReference("P", 1),
    delay: `${formatDate(new Date(), "yyyy-MM-dd")} / 08:30`,
    address: null,
    products: [
      {
        name: null,
        code: generateReference("PP-I", 11),
        quantity: 0,
      },
    ],
  },
];

const initDeliveries: ICourseDelivery[] = [
  {
    id: null,
    reference: null,
    pickupAddress: null,
    deliveryAddress: null,
    delay: `${formatDate(new Date(), "yyyy-MM-dd")} / 08:35`,
    products: [],
  },
];

export interface CourseProps {
  currentElement: number;
  setCurrentElement: React.Dispatch<React.SetStateAction<number>>;
  pickups: ICoursePickup[];
  setPickups: React.Dispatch<React.SetStateAction<ICoursePickup[]>>;
  deliveries: ICourseDelivery[];
  setDeliveries: React.Dispatch<React.SetStateAction<ICourseDelivery[]>>;
  addPickup: () => void;
  addDelivery: () => void;
  deletePickup: (pickupIndex: number | any) => void;
  addProductToPickup: (index: number | any) => void;
  deleteProductFromPickup: (pickupIndex: number | any, prodcutIndex: number) => void;
  editPickup: (pickupIndex: number | any, attributeName: string, value: any, producIndex?: number) => void;
  editDelivery: (deliveryIndex: number | any, attributeName: string, value: any, reference?: string) => void;
  deleteDelivery: (deliveryIndex: number | any) => void;
  initialReset: () => void;
  isPickupValid: boolean;
  isDeliveryValid: boolean;
  isSubmitted: boolean;
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  reformatItems: () => void;
  allAvailableProducts: string[];
  allProducts: Iproduct[];
  setAllProducts: React.Dispatch<React.SetStateAction<Iproduct[]>>;
  setClientAddresses: React.Dispatch<React.SetStateAction<any[] | ClientRefsAddress[]>>;
  clientAddresses: any[] | ClientRefsAddress[];
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setAllClients: React.Dispatch<React.SetStateAction<any[]>>;
  allClients: any[];
  validateCourseDateAndTime: (deliveryIndex: number) => any;
}
export const CourseContext = React.createContext<CourseProps>({
  currentElement: null,
  setCurrentElement: () => {},
  pickups: initPickups,
  setPickups: () => {},
  deliveries: initDeliveries,
  setDeliveries: () => {},
  addPickup: () => {},
  addDelivery: () => {},
  deletePickup: (pickupIndex: number) => {},
  deleteDelivery: (deliveryindex: number) => {},
  addProductToPickup: (pickupIndex: number | any) => {},
  deleteProductFromPickup: (pickupIndex: number | any, prodcutIndex: number) => {},
  editPickup: (pickupIndex: number | any, attributeName: string, value: any, producIndex?: number) => {},
  editDelivery: (deliveryIndex: number | any, attributeName: string, value: any, reference?: string) => {},
  initialReset: () => {},
  isPickupValid: false,
  isDeliveryValid: false,
  isSubmitted: false,
  setIsSubmitted: () => {},
  reformatItems: () => {},
  allAvailableProducts: [],
  allProducts: [],
  setAllProducts: () => {},
  setClientAddresses: () => {},
  clientAddresses: [],
  setIsLoading: () => {},
  isLoading: false,
  setAllClients: () => {},
  allClients: [],
  validateCourseDateAndTime: (deliveryIndex: number) => {},
});

export const useCourseContext = () => React.useContext(CourseContext);

const CourseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentElement, setCurrentElement] = React.useState<number>();
  const [pickups, setPickups] = React.useState<ICoursePickup[]>(initPickups);
  const [deliveries, setDeliveries] = React.useState<ICourseDelivery[]>(initDeliveries);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [allProducts, setAllProducts] = React.useState<Iproduct[]>([]);
  const [allAvailableProducts, setAllAvailableProducts] = React.useState<string[]>(
    deliveries
      ?.map((delivery: ICourseDelivery) => {
        return delivery?.products?.map((product: Iproduct) => product?.name);
      })
      ?.flat(1)
  );
  const [clientAddresses, setClientAddresses] = React.useState<ClientRefsAddress[] | any[]>([]);
  const [allClients, setAllClients] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const validateCourseDateAndTime = (deliveryIndex: number) => {
    const targetedPickup = pickups?.find(
      (pickup: ICoursePickup) => pickup?.reference === deliveries[deliveryIndex]?.reference
    );

    const targetedPickupDate = new Date(targetedPickup?.delay?.split("/")[0]);
    const currentDeliveryDate = new Date(deliveries[deliveryIndex]?.delay?.split("/")[0]);

    const [pickupHours, pickupMinutes] = targetedPickup?.delay?.split("/")[1]?.split(":") || [];
    const [deliveryHours, DeliveryMinutes] = deliveries[deliveryIndex]?.delay?.split("/")[1]?.split(":") || [];

    const date1 = new Date(new Date().getFullYear(), 0, 1, +pickupHours, +pickupMinutes);
    const date2 = new Date(new Date().getFullYear(), 0, 1, +deliveryHours, +DeliveryMinutes);

    return {
      isdelayValid: targetedPickupDate?.getTime() > currentDeliveryDate?.getTime(),
      isTimeValide: targetedPickupDate?.getTime() < currentDeliveryDate?.getTime() ? false : date1 >= date2,
      minDate: targetedPickup?.delay?.split("/")[0],
    };
  };

  const isPickupValid = useMemo(() => {
    const defaultPickup = pickups?.find((_, index) => index === pickups?.length - 1);
    if (currentElement !== undefined && pickups?.length) {
      const currentPickup = pickups?.find((_, index) => index === currentElement - 1);
      const { products, delay, address } = currentPickup || defaultPickup;
      const validDate = pickups?.every((pickup) => isDateValid(pickup.delay.split("/")[0]));

      const isAllProductsValid = products.length > 0 && products?.every((prod: Iproduct) => prod?.name);
      return (
        isAllProductsValid && validDate && !!delay && !!delay?.split("/")[1] && address?.address?.place_id !== undefined
      );
    }
  }, [currentElement, pickups]);

  const isDeliveryValid = useMemo(() => {
    const defaultDelivery = deliveries?.find((_, index) => index === deliveries?.length - 1);
    if (currentElement !== undefined && deliveries?.length) {
      const currentDelivery = deliveries?.find((_, index) => index === currentElement - pickups?.length - 1);
      const { products, delay, pickupAddress, deliveryAddress } = currentDelivery || defaultDelivery;
      const isAllProductsValid = products.length > 0 && products?.every((prod: Iproduct) => prod?.name);
      const validDate = deliveries.every(
        (delivery) => validateDeliveryDate(pickups, delivery) && isDateValid(delivery.delay.split("/")[0])
      );
      return (
        isAllProductsValid &&
        products?.length &&
        !!delay &&
        !!delay?.split("/")[1] &&
        pickupAddress?.address?.place_id !== undefined &&
        deliveryAddress?.address?.place_id !== undefined &&
        !validateCourseDateAndTime(pickups?.length - 1)?.isdelayValid &&
        !validateCourseDateAndTime(pickups?.length - 1)?.isTimeValide
      );
    }
  }, [currentElement, deliveries]);

  const addPickup = () => {
    setPickups((prev) => [
      ...prev,
      {
        id: null,
        reference: generateReference("P", pickups?.length + 1),
        delay: `${formatDate(new Date(), "yyyy-MM-dd")} / 08:30`,
        address: null,
        products: [{ name: null, code: generateReference("PP-I", 11), quantity: 0  }],
      },
    ]);
    setCurrentElement((prev) => prev + 1);
  };

  const editPickup = (pickupIndex: number | any, attributeName: string, value: any, producIndex?: number) => {
    const allPickups = [...pickups];
    const currentPickup = { ...allPickups[pickupIndex] };
    producIndex !== undefined && currentPickup
      ? (currentPickup.products[producIndex][attributeName] = value)
      : (currentPickup[attributeName] = value);
    allPickups[pickupIndex] = currentPickup;
    setPickups([...allPickups]);
  };

  const editDelivery = (deliveryIndex: number | any, attributeName: string, value: any, reference?: string) => {
    const allDeliveries = [...deliveries];
    const currentDelivery = { ...allDeliveries[deliveryIndex] };
    if (reference) {
      currentDelivery.reference = reference;
    }
    if (attributeName === "pickupAddress" && !value) {
      currentDelivery.products = [];
    }

    currentDelivery[attributeName] = value;
    allDeliveries[deliveryIndex] = currentDelivery;
    setDeliveries([...allDeliveries]);
  };

  const addProductToPickup = (pickupIndex: number | any) => {
    const allPickups = [...pickups];
    const currentPickup = { ...allPickups[pickupIndex] };
    const code = generateReference("PP-I", 11);
    currentPickup?.products?.push({ name: null, code, quantity: 0 });
    allPickups[pickupIndex] = currentPickup;
    setPickups([...allPickups]);
    const product = allPickups[pickupIndex]?.products?.find((product) => product.code === code);
    setAllProducts([...allProducts, product]);
  };

  const deleteProductFromPickup = (pickupIndex: number | any, producIndex: number) => {
    const allPickups = [...pickups];
    const currentPickup = { ...allPickups[pickupIndex] };

    currentPickup?.products?.splice(producIndex, 1);

    allPickups[pickupIndex] = currentPickup;
    setPickups([...allPickups]);
    const isProduct = allPickups[pickupIndex]?.products[producIndex]?.name !== null;

    if (isProduct) {
      const allDeliveries = [...deliveries];
      const deliveryIndex = allDeliveries.findIndex((delivery) => {
        return delivery?.pickupAddress?.place_id === currentPickup?.address?.place_id;
      });
      allDeliveries[deliveryIndex].products = currentPickup?.products?.filter((product) => {
        return allDeliveries[deliveryIndex].products?.filter(
          (delieveryProduct) => product?.code === delieveryProduct?.code
        );
      });

      allDeliveries[deliveryIndex] = allDeliveries.find((delivery) => {
        return delivery?.pickupAddress?.place_id === currentPickup?.address?.place_id;
      });

      setDeliveries([...allDeliveries]);
      setAllProducts(allDeliveries[deliveryIndex]?.products);
    }
  };

  const deletePickup = (pickupIndex: number | any) => {
    const allPickups = [...pickups];
    allPickups?.splice(pickupIndex, 1);
    setPickups([...allPickups]);
    setCurrentElement(pickups?.length - 1);
  };
  const deleteDelivery = (deliveryIndex: number | any) => {
    const allDeliveries = [...deliveries];
    allDeliveries?.splice(deliveryIndex, 1);
    setDeliveries([...allDeliveries]);
    setCurrentElement(deliveries?.length > 1 && pickups?.length + deliveries?.length - 1);
  };

  const addDelivery = () => {
    setDeliveries((prev) => [
      ...prev,
      {
        id: null,
        reference: null,
        pickupAddress: null,
        deliveryAddress: null,
        delay: `${formatDate(new Date(), "yyyy-MM-dd")} / 08:35`,
        products: [],
      },
    ]);
    setCurrentElement((prev) => prev + 1);
  };

  const initialReset = () => {
    setPickups(initPickups);
    setDeliveries(initDeliveries);
    setCurrentElement(null);
    setIsSubmitted(false);
  };

  const reformatItems = () => {
    setPickups(
      pickups?.map((pickup: ICoursePickup) => {
        return {
          id: null,
          reference: pickup?.reference,
          delay: pickup?.delay,
          address: null,
          products: pickup?.products,
        };
      })
    );
    setDeliveries(
      deliveries?.map((delivery: ICourseDelivery) => {
        return {
          id: null,
          reference: delivery?.reference,
          delay: delivery?.delay,
          deliveryAddress: null,
          pickupAddress: null,
          products: [],
        };
      })
    );
    setIsSubmitted(false);
  };

  useEffect(() => {
    setAllAvailableProducts(
      deliveries
        ?.map((delivery: ICourseDelivery) => {
          return delivery?.products?.map((product: Iproduct) => product?.name);
        })
        ?.flat(1)
    );
  }, [deliveries]);

  return (
    <CourseContext.Provider
      value={{
        currentElement,
        setCurrentElement,
        pickups,
        setPickups,
        deliveries,
        setDeliveries,
        addPickup,
        addDelivery,
        deletePickup,
        deleteDelivery,
        addProductToPickup,
        deleteProductFromPickup,
        editPickup,
        editDelivery,
        initialReset,
        isPickupValid,
        isDeliveryValid,
        isSubmitted,
        setIsSubmitted,
        reformatItems,
        allAvailableProducts,
        allProducts,
        setAllProducts,
        setClientAddresses,
        clientAddresses,
        setIsLoading,
        isLoading,
        setAllClients,
        allClients,
        validateCourseDateAndTime,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export default CourseProvider;
